import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import {
  LoadingIndicator,
  useIntersectionObserver,
  CmsEmptyList,
  Routes,
} from '@xolv-mybrightlink/common';
import { useNavigate } from 'react-router';
import { Box, List } from '@mui/material';
import { useFetchSavedEvents } from '../hooks/useFetchSavedEvents';
import { CmsEvent } from '../models/EventResult';
import EventItem from './EventItem';
import { removeBookmarkEvent } from '../hooks/useBookmarkEvent';

export const EventSavedList = () => {
  const [events, setEvents] = useState<CmsEvent[] | null>(null);
  const [hasLoadMore, setHasLoadMore] = useState<boolean>();
  const [currentPage, setCurrentPage] = useState(0);
  const [isRemovingBookmark, setIsRemovingBookmark] = useState(false);
  const navigate = useNavigate();
  const lastItemRef = useRef<HTMLDivElement>(null);
  const savedEventFetchResult = useFetchSavedEvents(currentPage, 10);

  const onLoadMore = useCallback(() => {
    if (hasLoadMore && savedEventFetchResult.type === 'DATA') {
      setCurrentPage((currentPage) => currentPage + 1);
    }
  }, [hasLoadMore, savedEventFetchResult.type]);

  useIntersectionObserver(lastItemRef, onLoadMore);

  useEffect(() => {
    if (savedEventFetchResult.type === 'DATA') {
      setEvents((currentEvents) => [
        ...(currentEvents ?? []),
        ...savedEventFetchResult.events,
      ]);
      setHasLoadMore(savedEventFetchResult.hasLoadMore);
    }
  }, [savedEventFetchResult]);

  const openEventDetails = useCallback(
    (event: CmsEvent) => {
      navigate(Routes.resources.events.postId(event.databaseId, 'all-events'));
    },
    [navigate],
  );

  const viewAllEvents = useCallback(() => {
    navigate(Routes.resources.events.index);
  }, [navigate]);

  const onBookmarkClicked = async (
    e: MouseEvent<SVGSVGElement>,
    event: CmsEvent,
    savedEvents: CmsEvent[],
    isRemoving: boolean,
  ) => {
    e.stopPropagation();
    if (!isRemoving) {
      setEvents(
        savedEvents.filter((item) => item.databaseId !== event.databaseId),
      );
      setIsRemovingBookmark(true);
      await removeBookmarkEvent(event.slug);
      setIsRemovingBookmark(false);
    }
  };

  return (
    <Box
      className='hide-scrollbar'
      sx={{
        height: '100%',
        overflowY: 'auto',
        justifyContent: 'center',
      }}
    >
      <List
        sx={{
          pl: 7.5,
          pr: 7.5,
        }}
      >
        {events?.map((item, index) => (
          <EventItem
            key={item.databaseId}
            event={item}
            ref={index === events.length - 1 ? lastItemRef : null}
            onEventClicked={() => openEventDetails(item)}
            onBookmarkClicked={(event) =>
              onBookmarkClicked(event, item, events, isRemovingBookmark)
            }
          />
        ))}
      </List>
      {savedEventFetchResult.type === 'LOADING' && (
        <LoadingIndicator
          sx={{
            height: !hasLoadMore ? '100%' : undefined,
            py: 10,
          }}
        />
      )}
      {events &&
        events.length === 0 &&
        savedEventFetchResult.type !== 'LOADING' && (
          <CmsEmptyList
            title='saved.event.empty'
            description='saved.event.empty-desc'
            action='saved.event.empty-view'
            onActionClicked={viewAllEvents}
          />
        )}
      {isRemovingBookmark && (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            top: 0,
            background: 'transparent',
            position: 'absolute',
          }}
        />
      )}
    </Box>
  );
};
